import React from 'react';
import { AnalysisResponse } from '../types';
import './AnalysisResults.css';

interface AnalysisResultsProps {
  results: AnalysisResponse;
  onNewAnalysis: () => void;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  results,
  onNewAnalysis,
}) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return '#e74c3c';
      case 'medium':
        return '#f39c12';
      case 'low':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return '#e74c3c';
      case 'medium':
        return '#f39c12';
      case 'low':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  return (
    <div>
      <div className="card results-header">
        <div className="header-content">
          <div>
            <h2>Analysis Results</h2>
            <p className="provider-info">
              Analyzed with <strong>{results.provider.toUpperCase()}</strong> on{' '}
              {new Date(results.timestamp).toLocaleString()}
            </p>
          </div>
          <button onClick={onNewAnalysis} className="new-analysis-button">
            New Analysis
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Risk Factors</h2>
        {results.risks.length === 0 ? (
          <p className="no-data">No risks analyzed.</p>
        ) : (
          <div className="factors-list">
            {results.risks.map((risk, index) => (
              <div key={index} className="factor-item">
                <div className="factor-header">
                  <h3>{risk.risk}</h3>
                  <span
                    className="badge"
                    style={{ backgroundColor: getSeverityColor(risk.severity) }}
                  >
                    {risk.severity.toUpperCase()}
                  </span>
                </div>
                <div className="factors">
                  <h4>Key Factors:</h4>
                  <ul>
                    {risk.factors.map((factor, fIndex) => (
                      <li key={fIndex}>{factor}</li>
                    ))}
                  </ul>
                </div>
                {risk.sources && risk.sources.length > 0 && (
                  <div className="sources">
                    <h4>Sources:</h4>
                    <ul>
                      {risk.sources.map((source, sIndex) => (
                        <li key={sIndex}>
                          <a
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Trend Factors</h2>
        {results.trends.length === 0 ? (
          <p className="no-data">No trends analyzed.</p>
        ) : (
          <div className="factors-list">
            {results.trends.map((trend, index) => (
              <div key={index} className="factor-item">
                <div className="factor-header">
                  <h3>{trend.trend}</h3>
                  <span
                    className="badge"
                    style={{ backgroundColor: getImpactColor(trend.impact) }}
                  >
                    {trend.impact.toUpperCase()} IMPACT
                  </span>
                </div>
                <div className="factors">
                  <h4>Key Factors:</h4>
                  <ul>
                    {trend.factors.map((factor, fIndex) => (
                      <li key={fIndex}>{factor}</li>
                    ))}
                  </ul>
                </div>
                {trend.sources && trend.sources.length > 0 && (
                  <div className="sources">
                    <h4>Sources:</h4>
                    <ul>
                      {trend.sources.map((source, sIndex) => (
                        <li key={sIndex}>
                          <a
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisResults;

